import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { CompanyDTO, CompanyFilterDTO, Result, TestPackageFilterDTO, UserDTO, WorkspaceDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { WorkspaceMaintenance } from './WorkspaceMaintenance';
import { WorkspaceFilterDTO } from '../../../shared/interfaces/testingSphere/WorkspaceFilterDTO';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { LocalStorange } from '../../../shared/environment';
import { create } from 'domain';
import { TestPackage } from '../test-package/TestPackage';

export const Workspace: React.FC = () => {

    const [internalResponsibleList, setInternalResposibleList] = useState<UserDTO[]>([]);
    const [externalResponsibleList, setExternalResposibleList] = useState<UserDTO[]>([]);
    const [createdByList, setCreatedList] = useState<UserDTO[]>([]);
    const [workspaceList, setWorkspaceList] = useState<WorkspaceDTO[]>([]);
    const [workspaceFilterDTO, setWorkspaceFilterDTO] = useState<WorkspaceFilterDTO>();
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [companyFilterDTO, setCompanyFilterDTO] = useState<CompanyFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setWorkspaceFilterDTO({
            ...workspaceFilterDTO,
            name: '',
            code: '',
            internalResponsibleId: '',
            externalResponsibleId: '',
            createdById: '',
            companyId: userInfo.user.companyId
        } as WorkspaceDTO);

        handleResponsibleGetAllViewAsync(setInternalResposibleList);
        handleResponsibleGetAllViewAsync(setExternalResposibleList);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            handleWorkspaceGetAllViewAsync();
        }, 5000);
        return () => clearInterval(interval);
    }, [workspaceFilterDTO]);

    useEffect(() => {
        handleWorkspaceGetAllViewAsync();
    }, [workspaceFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleWorkspaceGetAllViewAsync();
    }, [actionExecuted]);

    const handleResponsibleGetAllViewAsync = useCallback(async (setList: any) => {
        const userFilterDTO = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('corpsphere.people', userFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleWorkspaceGetAllViewAsync = useCallback(async () => {
        if(workspaceFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('testingsphere.workspace', workspaceFilterDTO, setActionExecuted, setLoading, setWorkspaceList, showMessage, true);
    }, [workspaceFilterDTO]);

    const handleClearFilterClick = () => {
        setWorkspaceFilterDTO({
            ...workspaceFilterDTO,
            name: '',   
            code: '',
            internalResponsibleId: '',
            externalResponsibleId: '',
            createdById: '',
            companyId: userInfo.user.companyId
        } as WorkspaceDTO);
    };

    function handleSearchClick() {
        handleWorkspaceGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const handleExecutionAction = (data: any) => {
        const testPackageFilterDTO: TestPackageFilterDTO = {
            workspaceId: data.id
        };
        GenericService.PostGenericAsync1('testingsphere.testpackage', 'StartExecutionAsync', testPackageFilterDTO, setActionExecuted, setLoading, showMessage);
        handleWorkspaceGetAllViewAsync();
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Workspace', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
        { id: 'ExecutionAction', toolTip: 'Start Execution', icon: 'play_circle_outline_outlined', iconColor: 'info', setAction: handleExecutionAction },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: workspaceList.length },
        { label: 'Name', value: workspaceFilterDTO?.name },
        { label: 'Code', value: workspaceFilterDTO?.code },
        { label: 'Internal Responsible', value: internalResponsibleList.find(x => x.id === workspaceFilterDTO?.internalResponsibleId)?.userName },
        { label: 'External Responsible', value: externalResponsibleList.find(x => x.id === workspaceFilterDTO?.externalResponsibleId)?.userName },
        { label: 'Created By', value: createdByList.find(x => x.id === workspaceFilterDTO?.createdById)?.userName },
    ];

    const columns: ICardColumns[] = [
        { field: 'code', label: 'Code', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'name', label: 'Name', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'internalResponsible', label: 'Internal Responsible', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'externalResponsible', label: 'External Responsible', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'totalScenarios', label: 'Total Scenarios', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'totalExecutedScenarios', label: 'Executed Scenarios', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosNoRun', label: 'Scenarios No Run', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosPending', label: 'Scenarios Pending', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosRunning', label: 'Scenarios Running', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosPassed', label: 'Scenarios Passed', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false, colorValue: 'green' },
        { field: 'TotalScenariosFailed', label: 'Scenarios Failed', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false, colorValue: 'red' },
        { field: 'TotalScenariosBlocked', label: 'Scenarios Blocked', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false, colorValue: 'yellow' },
        { field: 'TotalScenariosWarning', label: 'Scenarios Warning', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='WORKSPACE'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={10}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setWorkspaceFilterDTO}
                                value={workspaceFilterDTO?.name}
                                obj={workspaceFilterDTO}
                                placeholder={'Workspace'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='code'
                                label='Code'
                                setValue={setWorkspaceFilterDTO}
                                value={workspaceFilterDTO?.code}
                                obj={workspaceFilterDTO}
                                placeholder={'Code'}
                                maxLength={100}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='internalResponsibleId'
                                label='Interal Responsible'
                                setValue={setWorkspaceFilterDTO}
                                value={workspaceFilterDTO?.internalResponsibleId}
                                obj={workspaceFilterDTO}
                                valueList={internalResponsibleList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='externalResponsibleId'
                                label='External Responsible'
                                setValue={setWorkspaceFilterDTO}
                                value={workspaceFilterDTO?.externalResponsibleId}
                                obj={workspaceFilterDTO}
                                valueList={externalResponsibleList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='createdById'
                                label='Created By'
                                setValue={setWorkspaceFilterDTO}
                                value={workspaceFilterDTO?.createdById}
                                obj={workspaceFilterDTO}
                                valueList={createdByList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading === true ? (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                ) : (undefined)}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={workspaceList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'TEST PACKAGE'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <TestPackage
                                    data={expadedData}
                                />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <WorkspaceMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <WorkspaceMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.workspace'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};